/**
 * Public signup wizard — `<base>/signup`.
 *
 * Single-step self-service: visitor enters company name + their name +
 * email + password → backend creates tenant + owner user + provisions
 * Cloudflare DNS for `<slug>-rogue.geminii-ai.com` → returns auth token
 * → we redirect them straight into their new workspace pre-logged-in.
 *
 * No modal. No two-step token flow. Strict adherence to the "no modals"
 * rule.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2, Building2, Mail, User, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatErr } from "../lib/api";

const FEATURES = [
  "Multi-tenant CRM + job-card / field-service workflow",
  "AI assistant (Rogi) — voice, drafting, summarisation",
  "Customer portal with shareable links + tickets",
  "Inventory, procurement, sales pipeline, projects",
  "14-day free trial · no credit card required",
];

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | provisioning | success | error
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Sign up — Rogue · Geminii";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!company.trim() || !name.trim() || !email.trim() || pw.length < 8) {
      setErr("Please fill every field — password needs at least 8 characters.");
      return;
    }
    setStep("provisioning");
    try {
      const { data } = await api.post("/_geminii/tenants/instant-signup", {
        company_name: company.trim(),
        owner_email: email.trim(),
        owner_name: name.trim(),
        password: pw,
      });
      setResult(data);
      // Stash auth token under the FINAL tenant URL via location.hash so
      // we don't accidentally drop a session for the wrong tenant.
      setStep("success");
      // Auto-redirect after 4 seconds. Pass the JWT via URL fragment
      // (avoids exposing it in server logs) so the tenant app can
      // hydrate session storage without a second login.
      const tokenFrag = encodeURIComponent(data.token);
      setTimeout(() => {
        window.location.href = `${data.primary_url}/login#token=${tokenFrag}`;
      }, 4500);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail, "Signup couldn't complete. Try again."));
      setStep("error");
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[#0a0b0e] text-white"
      data-testid="signup-page"
    >
      {/* Hero bg */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-[40rem] h-[40rem] rounded-full bg-[#1FB6C1]/30 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[40rem] h-[40rem] rounded-full bg-[#A78BFA]/30 blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16 grid md:grid-cols-2 gap-12 md:gap-20">
        {/* Left: pitch */}
        <div className="space-y-8">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-[#1FB6C1] font-bold">
            <Sparkles className="w-4 h-4" /> Geminii Rogue
          </Link>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
              Spin up your<br />own workspace<br />
              <span className="text-[#1FB6C1]">in 60 seconds.</span>
            </h1>
            <p className="text-base text-[#9aa3b5] leading-relaxed max-w-md">
              Your team. Your branding. Your data. A complete CRM + field-service
              operating system, live on your own private subdomain the moment
              you click signup.
            </p>
          </div>

          <ul className="space-y-3" data-testid="signup-features">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-[#dde3ec]">
                <CheckCircle2 className="w-5 h-5 text-[#1FB6C1] shrink-0 mt-[2px]" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="text-xs text-[#647087] pt-6 border-t border-white/5">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1FB6C1] underline hover:text-white">
              Sign in →
            </Link>
          </div>
        </div>

        {/* Right: form / status */}
        <div className="md:pt-4">
          {step === "form" && (
            <form
              onSubmit={submit}
              className="bg-white/[0.03] border border-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl"
              data-testid="signup-form"
            >
              <h2 className="text-lg font-semibold mb-1">Start your free trial</h2>
              <p className="text-xs text-[#9aa3b5] mb-6">14 days · no credit card · cancel anytime</p>

              <Field icon={Building2} label="Company name">
                <input
                  type="text"
                  required
                  autoFocus
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Plumbing"
                  className="hv-input w-full"
                  data-testid="signup-company-input"
                />
              </Field>

              <Field icon={User} label="Your name">
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="hv-input w-full"
                  data-testid="signup-name-input"
                />
              </Field>

              <Field icon={Mail} label="Work email">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="hv-input w-full"
                  data-testid="signup-email-input"
                />
              </Field>

              <Field icon={KeyRound} label="Choose a password" hint="At least 8 characters">
                <input
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••"
                  className="hv-input w-full"
                  data-testid="signup-password-input"
                />
              </Field>

              {err && (
                <div
                  className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2"
                  data-testid="signup-error"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                className="hv-btn hv-btn-primary w-full mt-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
                data-testid="signup-submit-btn"
              >
                Create my workspace <ArrowRight className="w-4 h-4" />
              </button>

              <div className="text-[10px] text-[#647087] mt-4 leading-relaxed">
                By signing up you agree to the Acceptable Use Policy. You can delete
                your workspace any time from Settings.
              </div>
            </form>
          )}

          {step === "provisioning" && (
            <ProvisioningStatus />
          )}

          {step === "success" && result && (
            <SuccessCard result={result} />
          )}

          {step === "error" && (
            <ErrorCard err={err} onRetry={() => { setErr(""); setStep("form"); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, hint, children }) {
  return (
    <label className="block mb-3">
      <span className="text-[11px] uppercase tracking-[0.25em] text-[#647087] inline-flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" />
        {label}
        {hint && <span className="text-[#8a93a8] normal-case tracking-normal">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function ProvisioningStatus() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1200);
    return () => clearInterval(i);
  }, []);
  const steps = [
    "Allocating subdomain…",
    "Creating your workspace database…",
    "Provisioning Cloudflare DNS + SSL…",
    "Seeding default brand kit…",
    "Almost there…",
  ];
  return (
    <div
      className="bg-white/[0.03] border border-white/10 backdrop-blur-md rounded-2xl p-8 text-center"
      data-testid="signup-provisioning"
    >
      <Loader2 className="w-10 h-10 text-[#1FB6C1] animate-spin mx-auto mb-4" />
      <div className="text-sm font-semibold mb-2">Building your workspace…</div>
      <div className="text-xs text-[#9aa3b5] min-h-[1.2em]">
        {steps[tick % steps.length]}
      </div>
    </div>
  );
}

function SuccessCard({ result }) {
  return (
    <div
      className="bg-white/[0.03] border border-emerald-500/30 backdrop-blur-md rounded-2xl p-8"
      data-testid="signup-success"
    >
      <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
      <h3 className="text-base font-semibold text-center mb-1">Workspace live!</h3>
      <p className="text-xs text-[#9aa3b5] text-center mb-5">
        Your private subdomain has been provisioned. Redirecting you in 4 seconds…
      </p>
      <div className="bg-black/30 border border-white/10 rounded px-3 py-3 mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087] mb-1">Your workspace</div>
        <div className="font-mono text-sm text-[#1FB6C1] break-all">{result.primary_url}</div>
      </div>
      <div className="bg-black/30 border border-white/10 rounded px-3 py-3 mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087] mb-1">DNS status</div>
        <div className="text-xs text-[#dde3ec]">
          {result.dns_provisioning?.ok ? (
            <>
              <span className="text-emerald-300">✓ Cloudflare CNAME {result.dns_provisioning.status}</span>
              {" · SSL covered by free Universal certificate"}
            </>
          ) : result.dns_provisioning?.skipped ? (
            "DNS automation skipped — admin will provision shortly."
          ) : (
            <span className="text-amber-300">⚠ DNS provisioning fell back: {result.dns_provisioning?.error}</span>
          )}
        </div>
      </div>
      <a
        href={`${result.primary_url}/login#token=${encodeURIComponent(result.token || "")}`}
        className="hv-btn hv-btn-primary w-full text-sm font-semibold inline-flex items-center justify-center gap-2"
        data-testid="signup-go-to-workspace"
      >
        Go to my workspace <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

function ErrorCard({ err, onRetry }) {
  return (
    <div className="bg-white/[0.03] border border-rose-500/30 rounded-2xl p-8" data-testid="signup-error-card">
      <div className="text-sm font-semibold text-rose-300 mb-2">Couldn't create your workspace</div>
      <div className="text-xs text-[#9aa3b5] mb-5">{err}</div>
      <button
        type="button"
        onClick={onRetry}
        className="hv-btn hv-btn-primary w-full text-sm"
        data-testid="signup-retry-btn"
      >
        Try again
      </button>
    </div>
  );
}
